import { Platform } from 'react-native';
import { requireTracker, isTrackerReady } from './core';
import { isDebugSnowplowDisabled } from './debugState';

export interface InteractionElementContext {
  environment: 'app' | 'browser';
  tracking_name: string;
  element_type?: string | null;
  element_id?: string | null;
  element_class?: string | null;
  element_value?: string | null;
  step_position?: number;
  type_of_action?: string | null;
  step_name?: string | null;
  score?: number | null;
}

export interface UserInteractionEvent {
  trackingName: string;
  elementType?: string;
  elementId?: string;
  elementClass?: string;
  elementValue?: string;
  stepPosition?: number;
  typeOfAction?: string;
  stepName?: string;
  score?: number;
}

export async function trackUserInteraction(
  event: UserInteractionEvent,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): Promise<void> {
  if (isDebugSnowplowDisabled()) {
    console.log(`[snowplow][DEBUG] trackUserInteraction blocked - tracking_name: ${event.trackingName}`);
    return;
  }

  if (!isTrackerReady()) {
    console.warn('[snowplow][userInteraction] Tracker not ready, skipping user interaction event');
    return;
  }

  try {
    const tracker = requireTracker();

    // interaction_element/1-2-0 types step_position as an integer >= 1.
    // Anything else (0-based index, null, float) fails validation and sends
    // the whole event to the bad rows index: drop the field instead.
    const isValidStepPosition =
      typeof event.stepPosition === 'number' &&
      Number.isInteger(event.stepPosition) &&
      event.stepPosition >= 1;

    if (event.stepPosition !== undefined && !isValidStepPosition) {
      console.warn(
        `[snowplow][userInteraction] Dropping invalid step_position (${event.stepPosition}) ` +
        `for "${event.trackingName}": schema requires an integer >= 1`
      );
    }

    const interactionContext: InteractionElementContext = {
      environment: Platform.OS === 'web' ? 'browser' : 'app',
      tracking_name: event.trackingName,
      element_type: event.elementType ?? null,
      element_id: event.elementId ?? null,
      element_class: event.elementClass ?? null,
      element_value: event.elementValue ?? null,
      step_position: isValidStepPosition ? event.stepPosition : undefined,
      type_of_action: event.typeOfAction ?? null,
      step_name: event.stepName ?? null,
      score: event.score ?? null,
    };

    const contextEntities = [
      {
        schema: 'iglu:com.dgp/interaction_element/jsonschema/1-2-0',
        data: interactionContext,
      },
      ...(additionalContexts || []),
    ];

    const eventData = {
      schema: 'iglu:com.dgp/dv_user_interaction/jsonschema/1-0-0',
      data: {},
    };

    // Pass context as array directly, not wrapped in object
    tracker.trackSelfDescribingEvent(eventData, contextEntities);
  } catch (error) {
    console.error('[snowplow][userInteraction] Error tracking user interaction:', error);
    console.error('[snowplow][userInteraction] Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
  }
}

export async function trackAddToFavorites(
  contentId: number,
  contentType: 'serie' | 'episode',
  additionalContexts?: Array<{ schema: string; data: unknown }>
): Promise<void> {

  await trackUserInteraction(
    {
      trackingName: 'add_favorites',
      elementId: `${contentType}_${contentId}`,
      elementType: contentType,
    },
    additionalContexts
  );
}

export async function trackRemoveFromFavorites(
  contentId: number,
  contentType: 'serie' | 'episode',
  additionalContexts?: Array<{ schema: string; data: unknown }>
): Promise<void> {
  await trackUserInteraction(
    {
      trackingName: 'remove_favorites',
      elementId: `${contentType}_${contentId}`,
      elementType: contentType,
    },
    additionalContexts
  );
}

export function trackEarnCoins(
  coinsEarned: number,
  achievementType: string,
  metadata?: {
    episodeId?: string | number;
    seriesId?: string | number;
    seasonId?: string | number;
    rubricId?: string | number;
    [key: string]: any;
  },
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {

  try {
    trackUserInteraction(
      {
        trackingName: 'earn_coins',
        score: coinsEarned,
        elementType: achievementType,
        elementId: metadata?.episodeId?.toString() || metadata?.seriesId?.toString() || metadata?.seasonId?.toString() || metadata?.rubricId?.toString() || null,
        elementValue: metadata ? JSON.stringify(metadata) : null,
        typeOfAction: 'reward_earned',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][earn_coins] Error tracking earn_coins event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][earn_coins] Error in trackEarnCoins (non-blocking):', error);
  }
}

export function trackSpendCoins(
  coinsSpent: number,
  reason: string,
  metadata?: {
    episodeId?: string | number;
    cost?: number;
    contentType?: string;
    [key: string]: any;
  },
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'spend_coins',
        score: coinsSpent,
        elementType: reason,
        elementId: metadata?.episodeId?.toString() || null,
        elementValue: metadata ? JSON.stringify(metadata) : null,
        typeOfAction: 'coins_spent',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][spend_coins] Error tracking spend_coins event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][spend_coins] Error in trackSpendCoins (non-blocking):', error);
  }
}

export type PaywallSource = 'profile' | 'video_player' | 'deeplink' | 'episode_screen';
export type PaywallDismissMethod = 'button' | 'background_tap' | 'swipe' | 'purchase_complete';

export interface PaywallProductInfo {
  productId: string;
  productTitle?: string;
  localizedPrice?: string;
  currencyCode?: string;
}

export function trackPaywallOpen(
  source: PaywallSource,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'paywall_open',
        elementType: source,
        typeOfAction: 'view',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][paywall_open] Error tracking paywall_open event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][paywall_open] Error in trackPaywallOpen (non-blocking):', error);
  }
}

export function trackPaywallClose(
  source: PaywallSource,
  dismissMethod: PaywallDismissMethod,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'paywall_close',
        elementType: source,
        elementId: dismissMethod,
        typeOfAction: 'dismiss',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][paywall_close] Error tracking paywall_close event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][paywall_close] Error in trackPaywallClose (non-blocking):', error);
  }
}

export function trackPaywallProductClick(
  product: PaywallProductInfo,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'paywall_product_click',
        elementType: 'subscription',
        elementId: product.productId,
        elementValue: JSON.stringify({
          title: product.productTitle,
          price: product.localizedPrice,
          currency: product.currencyCode,
        }),
        typeOfAction: 'click',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][paywall_product_click] Error tracking paywall_product_click event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][paywall_product_click] Error in trackPaywallProductClick (non-blocking):', error);
  }
}

export function trackPurchaseInitiated(
  product: PaywallProductInfo,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'purchase_initiated',
        elementType: 'subscription',
        elementId: product.productId,
        elementValue: JSON.stringify({
          title: product.productTitle,
          price: product.localizedPrice,
          currency: product.currencyCode,
        }),
        typeOfAction: 'purchase_start',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][purchase_initiated] Error tracking purchase_initiated event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][purchase_initiated] Error in trackPurchaseInitiated (non-blocking):', error);
  }
}

export function trackPurchaseSuccess(
  product: PaywallProductInfo,
  transactionInfo?: {
    transactionId?: string;
    originalTransactionId?: string;
    purchaseDate?: string;
  },
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'purchase_success',
        elementType: 'subscription',
        elementId: product.productId,
        elementValue: JSON.stringify({
          title: product.productTitle,
          price: product.localizedPrice,
          currency: product.currencyCode,
          transactionId: transactionInfo?.transactionId,
          originalTransactionId: transactionInfo?.originalTransactionId,
          purchaseDate: transactionInfo?.purchaseDate,
        }),
        typeOfAction: 'purchase_complete',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][purchase_success] Error tracking purchase_success event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][purchase_success] Error in trackPurchaseSuccess (non-blocking):', error);
  }
}

export function trackPurchaseCancel(
  product: PaywallProductInfo,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'purchase_cancel',
        elementType: 'subscription',
        elementId: product.productId,
        elementValue: JSON.stringify({
          title: product.productTitle,
          price: product.localizedPrice,
          currency: product.currencyCode,
        }),
        typeOfAction: 'purchase_cancelled',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][purchase_cancel] Error tracking purchase_cancel event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][purchase_cancel] Error in trackPurchaseCancel (non-blocking):', error);
  }
}

export function trackPurchaseFailed(
  product: PaywallProductInfo,
  errorInfo: {
    errorMessage?: string;
    errorCode?: string;
  },
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'purchase_failed',
        elementType: 'subscription',
        elementId: product.productId,
        elementValue: JSON.stringify({
          title: product.productTitle,
          price: product.localizedPrice,
          currency: product.currencyCode,
          errorMessage: errorInfo.errorMessage,
          errorCode: errorInfo.errorCode,
        }),
        typeOfAction: 'purchase_error',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][purchase_failed] Error tracking purchase_failed event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][purchase_failed] Error in trackPurchaseFailed (non-blocking):', error);
  }
}

export function trackRestorePurchases(
  status: 'initiated' | 'success' | 'failed',
  restoredProductId?: string,
  errorMessage?: string,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: `restore_${status}`,
        elementType: 'subscription',
        elementId: restoredProductId || undefined,
        elementValue: errorMessage ? JSON.stringify({ errorMessage }) : undefined,
        typeOfAction: `restore_${status}`,
      },
      additionalContexts
    ).catch(error => {
      console.error(`[snowplow][restore_${status}] Error tracking restore event (non-blocking):`, error);
    });
  } catch (error) {
    console.error(`[snowplow][restore_${status}] Error in trackRestorePurchases (non-blocking):`, error);
  }
}

export function trackPaywallLearnMore(
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'paywall_learn_more',
        elementType: 'button',
        elementId: 'learn_more_btn',
        typeOfAction: 'click',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][paywall_learn_more] Error tracking paywall_learn_more event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][paywall_learn_more] Error in trackPaywallLearnMore (non-blocking):', error);
  }
}

export type SearchSource = 'header' | 'homepage';

export function trackSearchOpen(
  source: SearchSource = 'header',
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'search_open',
        elementType: 'search_icon',
        elementId: `search_${source}`,
        typeOfAction: 'click',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][search_open] Error tracking search_open event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][search_open] Error in trackSearchOpen (non-blocking):', error);
  }
}

export function trackSearchClose(
  source: SearchSource = 'header',
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'search_close',
        elementType: 'search_icon',
        elementId: `search_${source}`,
        typeOfAction: 'close',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][search_close] Error tracking search_close event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][search_close] Error in trackSearchClose (non-blocking):', error);
  }
}

export function trackSearchQuery(
  query: string,
  resultsCount: number,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'search_query',
        elementType: 'search_input',
        elementValue: query,
        score: resultsCount,
        typeOfAction: 'search',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][search_query] Error tracking search_query event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][search_query] Error in trackSearchQuery (non-blocking):', error);
  }
}

export type SearchResultType = 'serie' | 'episode';

export function trackSearchResultClick(
  resultId: string,
  resultType: SearchResultType,
  resultTitle: string,
  resultPosition: number,
  query: string,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'search_result_click',
        elementType: resultType,
        elementId: resultId,
        elementValue: JSON.stringify({
          title: resultTitle,
          query: query,
        }),
        stepPosition: resultPosition,
        typeOfAction: 'click',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][search_result_click] Error tracking search_result_click event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][search_result_click] Error in trackSearchResultClick (non-blocking):', error);
  }
}

export function trackRubriqueClick(
  rubriqueId: string | null,
  rubriqueName: string,
  position?: number,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    const isAllRubrique = rubriqueId === null;
    trackUserInteraction(
      {
        trackingName: isAllRubrique ? 'rubrique_all_click' : 'rubrique_click',
        elementType: 'rubrique_filter',
        elementId: rubriqueId || 'all',
        elementValue: rubriqueName,
        stepPosition: position,
        typeOfAction: 'filter',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][rubrique_click] Error tracking rubrique_click event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][rubrique_click] Error in trackRubriqueClick (non-blocking):', error);
  }
}

export type GamificationIntroAction = 'signup' | 'login' | 'close';

export function trackGamificationIntroOpen(
  source: 'homepage_header' = 'homepage_header',
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'gamification_intro_open',
        elementType: 'gamification_modal',
        elementId: source,
        typeOfAction: 'view',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][gamification_intro_open] Error tracking gamification_intro_open event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][gamification_intro_open] Error in trackGamificationIntroOpen (non-blocking):', error);
  }
}

export function trackGamificationIntroAction(
  action: GamificationIntroAction,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: `gamification_intro_${action}`,
        elementType: 'gamification_modal',
        elementId: `${action}_button`,
        typeOfAction: action === 'close' ? 'dismiss' : 'click',
      },
      additionalContexts
    ).catch(error => {
      console.error(`[snowplow][gamification_intro_${action}] Error tracking event (non-blocking):`, error);
    });
  } catch (error) {
    console.error(`[snowplow][gamification_intro_${action}] Error in trackGamificationIntroAction (non-blocking):`, error);
  }
}

export type RatingPromptMilestone = 5 | 50 | 200;

export function trackRatingPromptShown(
  milestone: RatingPromptMilestone,
  completedVideosCount: number,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'rating_prompt_shown',
        elementType: 'rating_modal',
        elementId: `milestone_${milestone}`,
        typeOfAction: 'view',
        score: completedVideosCount,
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][rating_prompt_shown] Error tracking event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][rating_prompt_shown] Error in trackRatingPromptShown (non-blocking):', error);
  }
}

export function trackRatingPromptResult(
  milestone: RatingPromptMilestone,
  result: 'displayed' | 'unavailable',
  completedVideosCount: number,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: `rating_prompt_${result}`,
        elementType: 'rating_modal',
        elementId: `milestone_${milestone}`,
        typeOfAction: result === 'displayed' ? 'display' : 'error',
        score: completedVideosCount,
      },
      additionalContexts
    ).catch(error => {
      console.error(`[snowplow][rating_prompt_${result}] Error tracking event (non-blocking):`, error);
    });
  } catch (error) {
    console.error(`[snowplow][rating_prompt_${result}] Error in trackRatingPromptResult (non-blocking):`, error);
  }
}

export type EpisodeModalTab = 'synopsis' | 'episodes';

export function trackEpisodeModalOpen(
  seriesId?: string,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'episode_modal_open',
        elementType: 'episode_modal',
        elementId: seriesId || undefined,
        typeOfAction: 'view',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][episode_modal_open] Error tracking event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][episode_modal_open] Error in trackEpisodeModalOpen (non-blocking):', error);
  }
}

export function trackEpisodeModalClose(
  seriesId?: string,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: 'episode_modal_close',
        elementType: 'episode_modal',
        elementId: seriesId || undefined,
        typeOfAction: 'dismiss',
      },
      additionalContexts
    ).catch(error => {
      console.error('[snowplow][episode_modal_close] Error tracking event (non-blocking):', error);
    });
  } catch (error) {
    console.error('[snowplow][episode_modal_close] Error in trackEpisodeModalClose (non-blocking):', error);
  }
}

export function trackEpisodeModalTabClick(
  tab: EpisodeModalTab,
  seriesId?: string,
  additionalContexts?: Array<{ schema: string; data: unknown }>
): void {
  try {
    trackUserInteraction(
      {
        trackingName: `episode_modal_tab_${tab}`,
        elementType: 'episode_modal_tab',
        elementId: `${tab}_tab`,
        elementValue: seriesId || undefined,
        typeOfAction: 'click',
      },
      additionalContexts
    ).catch(error => {
      console.error(`[snowplow][episode_modal_tab_${tab}] Error tracking event (non-blocking):`, error);
    });
  } catch (error) {
    console.error(`[snowplow][episode_modal_tab_${tab}] Error in trackEpisodeModalTabClick (non-blocking):`, error);
  }
}
